import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import HoursOfOperationForm from '../components/admin/HoursOfOperationForm';

// ─── Icons ───────────────────────────────────────────────────
const CheckIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
  </svg>
);

const EyeIcon = ({ open }) =>
  open ? (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  ) : (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
  );

// ─── Step Indicator ──────────────────────────────────────────
const StepIndicator = ({ step, totalSteps, labels }) => (
  <div className="flex items-center gap-0 mb-8">
    {Array.from({ length: totalSteps }, (_, i) => {
      const idx = i + 1;
      const isCompleted = step > idx;
      const isCurrent = step === idx;
      return (
        <div key={idx} className="flex items-center flex-1 last:flex-none">
          <div className="flex flex-col items-center">
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center text-[13px] font-bold transition-all duration-300 ${
                isCompleted
                  ? 'bg-indigo-600 text-white shadow-md shadow-indigo-200'
                  : isCurrent
                  ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-300 ring-4 ring-indigo-100'
                  : 'bg-slate-100 text-slate-400'
              }`}
            >
              {isCompleted ? <CheckIcon /> : idx}
            </div>
            <span
              className={`mt-1.5 text-[11px] font-semibold whitespace-nowrap ${
                isCurrent ? 'text-indigo-600' : isCompleted ? 'text-indigo-500' : 'text-slate-400'
              }`}
            >
              {labels[i]}
            </span>
          </div>
          {idx < totalSteps && (
            <div
              className={`flex-1 h-[2px] mx-2 mb-5 transition-all duration-300 ${
                step > idx ? 'bg-indigo-500' : 'bg-slate-200'
              }`}
            />
          )}
        </div>
      );
    })}
  </div>
);

// ─── Input Field ─────────────────────────────────────────────
const Field = ({ label, required, error, hint, children }) => (
  <div className="space-y-1">
    <label className="block text-[12px] font-semibold text-slate-700">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
    {hint && !error && <p className="text-[11px] text-slate-400">{hint}</p>}
    {error && <p className="text-[11px] text-red-500 flex items-center gap-1">
      <span>⚠</span> {error}
    </p>}
  </div>
);

const inputCls = (hasErr) =>
  `w-full px-3 py-2.5 text-[13px] text-slate-800 placeholder-slate-400 border rounded-lg focus:outline-none focus:ring-2 transition-all duration-200 ${
    hasErr
      ? 'border-red-300 bg-red-50 focus:border-red-400 focus:ring-red-100'
      : 'border-slate-200 bg-white focus:border-indigo-400 focus:ring-indigo-100 hover:border-slate-300'
  }`;

// ─── Main Page ────────────────────────────────────────────────

const TOTAL_STEPS = 4;
const STEP_LABELS = ['Your Account', 'Business Info', 'Business Hours', 'Location'];

const INITIAL_FORM = {
  // Step 1
  owner_name: '',
  owner_email: '',
  owner_password: '',
  confirm_password: '',
  // Step 2
  business_name: '',
  business_category: '',
  website_url: '',
  phone_number: '',
  email_business: '',
  business_description: '',
  // Step 3 - Business Hours
  opening_hours: null,
  // Step 4 - Location
  business_logo_url: '',
  street_address: '',
  city: '',
  state_province: '',
  postal_code: '',
  country: '',
};

const BusinessRegistrationPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [step, setStep] = useState(1);
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [categories, setCategories] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');
  const [success, setSuccess] = useState(false);

  // Fetch categories from public API
  useEffect(() => {
    const controller = new AbortController();
    fetch('https://vmc-backend-vt2w.onrender.com/api/categories', { signal: controller.signal })
      .then((r) => r.json())
      .then((d) => setCategories(d.data || []))
      .catch(() => {}); // Silently fail — category list is still useful even if empty
    return () => controller.abort();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    if (serverError) setServerError('');
  };

  const handleHoursChange = (newHours) => {
    setForm((prev) => ({ ...prev, opening_hours: newHours }));
    if (errors.opening_hours) setErrors((prev) => ({ ...prev, opening_hours: '' }));
  };

  // ─── Per-step validation ───────────────────────────────────
  const validateStep = (s) => {
    const errs = {};

    if (s === 1) {
      if (!form.owner_name.trim() || form.owner_name.trim().length < 2)
        errs.owner_name = 'Full name must be at least 2 characters';
      if (!form.owner_email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.owner_email))
        errs.owner_email = 'Enter a valid email address';
      if (!form.owner_password || form.owner_password.length < 8)
        errs.owner_password = 'Password must be at least 8 characters';
      if (form.confirm_password !== form.owner_password)
        errs.confirm_password = 'Passwords do not match';
    }

    if (s === 2) {
      if (!form.business_name.trim() || form.business_name.trim().length < 3)
        errs.business_name = 'Business name must be at least 3 characters';
      if (!form.business_category)
        errs.business_category = 'Please select a category';
      if (!form.website_url.trim()) {
        errs.website_url = 'Website URL is required';
      } else {
        try { new URL(form.website_url); } catch {
          errs.website_url = 'Enter a valid URL (e.g. https://example.com)';
        }
      }
      if (!form.phone_number.trim() || !/[\d\s\-\+\(\)]{10,}/.test(form.phone_number.replace(/\s/g, '')))
        errs.phone_number = 'Enter a valid phone number (min 10 digits)';
      if (!form.email_business.trim())
        errs.email_business = 'Business email is required';
      if (form.email_business && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email_business))
        errs.email_business = 'Enter a valid email address';
      // Domain matching validation
      if (form.email_business && form.website_url) {
        const emailDomain = form.email_business.split('@')[1]?.toLowerCase();
        const getDomain = (url) => {
          try {
            return new URL(url).hostname.replace('www.', '').toLowerCase();
          } catch { return ''; }
        };
        const urlDomain = getDomain(form.website_url);
        if (emailDomain && urlDomain && !emailDomain.includes(urlDomain) && !urlDomain.includes(emailDomain)) {
          errs.email_business = 'Business email domain must match your website domain (e.g., if website is tajhotels.in, use contact@tajhotels.in)';
        }
      }
      if (!form.business_description.trim() || form.business_description.trim().length < 20)
        errs.business_description = 'Description must be at least 20 characters';
      if (form.business_description.length > 1000)
        errs.business_description = 'Description must be under 1000 characters';
    }

    if (s === 3) {
      // Hours validation — optional step, no required errors
      // Hours are validated by HoursOfOperationForm component
    }

    if (s === 4) {
      if (!form.street_address.trim() || form.street_address.trim().length < 5)
        errs.street_address = 'Street address must be at least 5 characters';
      if (!form.city.trim() || form.city.trim().length < 2)
        errs.city = 'City is required';
      if (!form.country.trim() || form.country.trim().length < 2)
        errs.country = 'Country is required';
      if (!form.business_logo_url.trim())
        errs.business_logo_url = 'Business logo URL is required';
      else {
        try { new URL(form.business_logo_url); } catch {
          errs.business_logo_url = 'Enter a valid URL';
        }
      }
    }

    return errs;
  };

  const handleNext = () => {
    const errs = validateStep(step);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setStep((s) => s + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setErrors({});
    setStep((s) => s - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    const errs = validateStep(4);
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSubmitting(true);
    setServerError('');

    const payload = {
      owner_name: form.owner_name.trim(),
      owner_email: form.owner_email.trim().toLowerCase(),
      owner_password: form.owner_password,
      business_name: form.business_name.trim(),
      business_category: form.business_category,
      website_url: form.website_url.trim(),
      phone_number: form.phone_number.trim(),
      email_business: form.email_business.trim() || undefined,
      business_description: form.business_description.trim(),
      ...(form.opening_hours ? { opening_hours: form.opening_hours } : {}),
      business_logo_url: form.business_logo_url.trim() || undefined,
      street_address: form.street_address.trim(),
      city: form.city.trim(),
      state_province: form.state_province.trim() || undefined,
      postal_code: form.postal_code.trim() || undefined,
      country: form.country.trim(),
    };

    try {
      const response = await fetch('https://vmc-backend-vt2w.onrender.com/api/businesses/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        setServerError(data.message || 'Submission failed. Please try again.');
        return;
      }

      setSuccess(true);
    } catch {
      setServerError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Success Screen ────────────────────────────────────────
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50 flex items-center justify-center p-6">
        <div className="max-w-[480px] w-full text-center space-y-6">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <h1 className="text-[26px] font-bold text-slate-900">Application Submitted!</h1>
            <p className="text-[14px] text-slate-500 mt-2 leading-relaxed">
              Thank you for registering your business. Our team will review your application and
              get back to you within <strong>2–3 business days</strong>.
            </p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5 text-left space-y-3">
            <p className="text-[12px] font-bold text-slate-600 uppercase tracking-wide">What happens next?</p>
            {[
              'Our team reviews your business details',
            //   'You receive an approval email',
              'Your business goes live on VMC Reviews',
              'Customers can start leaving reviews',
            ].map((step, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[11px] font-bold text-indigo-600">{i + 1}</span>
                </div>
                <p className="text-[13px] text-slate-600">{step}</p>
              </div>
            ))}
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/')}
              className="flex-1 px-4 py-2.5 text-[13px] font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
              Back to Home
            </button>
            <button
              onClick={() => navigate('/businesses')}
              className="flex-1 px-4 py-2.5 text-[13px] font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Browse Businesses
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Main Layout ───────────────────────────────────────────
  return (
    <div className="min-h-[calc(100vh-62px)] bg-slate-100 px-4 py-6 sm:px-6 sm:py-10 lg:px-8 lg:py-12">
      <div className="mx-auto flex min-h-[78vh] w-full max-w-7xl overflow-hidden rounded-2xl bg-white shadow-2xl shadow-slate-300/35 ring-1 ring-slate-200 lg:min-h-[82vh]">
        {/* Left Panel */}
        <section className="relative hidden w-3/5 overflow-hidden bg-[#0F172A] p-10 text-white lg:flex lg:flex-col xl:p-14">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -left-24 -top-20 h-72 w-72 rounded-full bg-indigo-500/20 blur-3xl" />
            <div className="absolute -right-20 bottom-12 h-72 w-72 rounded-full bg-sky-500/10 blur-3xl" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(255,255,255,0.07),transparent_40%),radial-gradient(circle_at_85%_80%,rgba(91,79,207,0.2),transparent_45%)]" />
          </div>

          <div className="relative z-10 flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 shadow-lg shadow-indigo-900/40">
              <span className="text-sm font-black tracking-tight">VMC</span>
            </div>
            <div className="leading-none">
              <p className="text-base font-bold tracking-tight">VMC Reviews</p>
              <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-300">Business Platform</p>
            </div>
          </div>

          <div className="relative z-10 mt-20 max-w-lg xl:mt-24">
            <h1 className="text-5xl font-extrabold leading-[1.1] tracking-tight">Register Your Business</h1>
            <p className="mt-5 max-w-md text-base leading-7 text-slate-300">
              Create your business profile, collect verified reviews, and build trust with local customers.
            </p>
          </div>

          <div className="relative z-10 mt-auto grid grid-cols-3 gap-3 pt-10">
            <div className="flex min-w-0 items-center gap-3 rounded-xl border border-white/15 bg-white/5 px-4 py-3 backdrop-blur-sm">
              <svg className="h-5 w-5 text-indigo-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 7h18M7 3h10a2 2 0 012 2v14a2 2 0 01-2 2H7a2 2 0 01-2-2V5a2 2 0 012-2z" />
              </svg>
              <span className="text-[13px] font-medium leading-tight text-slate-100">Guided 4-step setup</span>
            </div>
            <div className="flex min-w-0 items-center gap-3 rounded-xl border border-white/15 bg-white/5 px-4 py-3 backdrop-blur-sm">
              <svg className="h-5 w-5 text-indigo-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.5 2A8.5 8.5 0 113.5 12a8.5 8.5 0 0117 0z" />
              </svg>
              <span className="text-[13px] font-medium leading-tight text-slate-100">Verified listing process</span>
            </div>
            <div className="flex min-w-0 items-center gap-3 rounded-xl border border-white/15 bg-white/5 px-4 py-3 backdrop-blur-sm">
              <svg className="h-5 w-5 text-indigo-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <span className="text-[13px] font-medium leading-tight text-slate-100">Manage from dashboard</span>
            </div>
          </div>
        </section>

        {/* Right Panel */}
        <section className="flex w-full items-center bg-white px-5 py-10 sm:px-8 md:px-10 lg:w-2/5 lg:px-12 xl:px-14">
          <div className="mx-auto w-full max-w-[520px]">
            <div className="mb-8 lg:hidden">
              <div className="flex items-center gap-2.5">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 shadow-md shadow-indigo-300/50">
                  <span className="text-sm font-black tracking-tight text-white">VMC</span>
                </div>
                <div>
                  <p className="text-[15px] font-bold tracking-tight text-slate-900">VMC Reviews</p>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Business Platform</p>
                </div>
              </div>
            </div>

            <div className="mb-4 text-sm text-slate-500">
              Already registered?{' '}
              <Link to="/login" className="font-semibold text-[#5B4FCF] transition-colors hover:text-[#4A3FC2]">
                Sign in
              </Link>
            </div>

            {/* Header */}
            <div className="mb-7">
              <h2 className="text-[22px] font-black text-slate-900 tracking-tight">
                {step === 1 && 'Create your account'}
                {step === 2 && 'Tell us about your business'}
                {step === 3 && 'What are your business hours?'}
                {step === 4 && 'Where is your business located?'}
              </h2>
              <p className="text-[13px] text-slate-500 mt-1">
                {step === 1 && 'Your account details to log in and manage your listing.'}
                {step === 2 && 'Help customers find and understand your business.'}
                {step === 3 && 'Let customers know when you are open (optional).'}
                {step === 4 && "Last step — add your address and business logo."}
              </p>
            </div>

            {/* Step Indicator */}
            <StepIndicator step={step} totalSteps={TOTAL_STEPS} labels={STEP_LABELS} />

            {/* Server Error */}
            {serverError && (
              <div className="mb-5 p-3.5 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p className="text-[13px] text-red-700">{serverError}</p>
              </div>
            )}

            {/* ── STEP 1: Account ── */}
            {step === 1 && (
              <div className="space-y-4">
                <Field label="Full Name" required error={errors.owner_name}>
                  <input
                    type="text"
                    name="owner_name"
                    value={form.owner_name}
                    onChange={handleChange}
                    placeholder="Rajesh Kumar"
                    autoComplete="name"
                    className={inputCls(!!errors.owner_name)}
                  />
                </Field>

                <Field label="Email Address" required error={errors.owner_email}>
                  <input
                    type="email"
                    name="owner_email"
                    value={form.owner_email}
                    onChange={handleChange}
                    placeholder="rajesh@tajhotels.co.in"
                    autoComplete="email"
                    className={inputCls(!!errors.owner_email)}
                  />
                </Field>

                <Field
                  label="Password"
                  required
                  error={errors.owner_password}
                  hint="Minimum 8 characters"
                >
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="owner_password"
                      value={form.owner_password}
                      onChange={handleChange}
                      placeholder="Create a strong password"
                      autoComplete="new-password"
                      className={`${inputCls(!!errors.owner_password)} pr-10`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      <EyeIcon open={showPassword} />
                    </button>
                  </div>
                  {/* Strength bar */}
                  {form.owner_password && (
                    <div className="mt-2 flex gap-1">
                      {[1, 2, 3, 4].map((lvl) => {
                        const strength = Math.min(4, Math.floor(form.owner_password.length / 3));
                        return (
                          <div
                            key={lvl}
                            className={`h-1 flex-1 rounded-full transition-colors ${
                              lvl <= strength
                                ? strength <= 1 ? 'bg-red-400' : strength <= 2 ? 'bg-amber-400' : strength <= 3 ? 'bg-yellow-400' : 'bg-green-500'
                                : 'bg-slate-200'
                            }`}
                          />
                        );
                      })}
                    </div>
                  )}
                </Field>

                <Field label="Confirm Password" required error={errors.confirm_password}>
                  <div className="relative">
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      name="confirm_password"
                      value={form.confirm_password}
                      onChange={handleChange}
                      placeholder="Re-enter your password"
                      autoComplete="new-password"
                      className={`${inputCls(!!errors.confirm_password)} pr-10`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      <EyeIcon open={showConfirm} />
                    </button>
                    {form.confirm_password && form.confirm_password === form.owner_password && (
                      <div className="absolute right-8 top-1/2 -translate-y-1/2 text-green-500">
                        <CheckIcon />
                      </div>
                    )}
                  </div>
                </Field>

                {/* <p className="text-[11px] text-slate-400 leading-relaxed pt-1">
                  By continuing you agree to our{' '}
                  <Link to="/" className="text-indigo-600 hover:underline">Terms of Service</Link>
                  {' '}and{' '}
                  <Link to="/" className="text-indigo-600 hover:underline">Privacy Policy</Link>.
                </p> */}
              </div>
            )}

            {/* ── STEP 2: Business Info ── */}
            {step === 2 && (
              <div className="space-y-4">
                <Field label="Business Name" required error={errors.business_name}>
                  <input
                    type="text"
                    name="business_name"
                    value={form.business_name}
                    onChange={handleChange}
                    placeholder="e.g., Taj Hotels Pvt Ltd, Flipkart India Pvt Ltd"
                    className={inputCls(!!errors.business_name)}
                  />
                </Field>

                <Field label="Business Category" required error={errors.business_category}>
                  <div className="relative">
                    <select
                      name="business_category"
                      value={form.business_category}
                      onChange={handleChange}
                      className={`${inputCls(!!errors.business_category)} appearance-none pr-8`}
                    >
                      <option value="">Select a category...</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                    <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </Field>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Website URL" required error={errors.website_url}>
                    <input
                      type="url"
                      name="website_url"
                      value={form.website_url}
                      onChange={handleChange}
                      placeholder="https://tajhotels.in"
                      className={inputCls(!!errors.website_url)}
                    />
                  </Field>
                  <Field label="Phone Number" required error={errors.phone_number}>
                    <input
                      type="tel"
                      name="phone_number"
                      value={form.phone_number}
                      onChange={handleChange}
                      placeholder="+91 98765 43210"
                      className={inputCls(!!errors.phone_number)}
                    />
                  </Field>
                </div>

                <Field label="Business Email" required error={errors.email_business}>
                  <input
                    type="email"
                    name="email_business"
                    value={form.email_business}
                    onChange={handleChange}
                    placeholder="contact@tajhotels.in"
                    className={inputCls(!!errors.email_business)}
                  />
                </Field>

                <Field
                  label="Business Description"
                  required
                  error={errors.business_description}
                  hint={`${form.business_description.length}/1000 characters`}
                >
                  <textarea
                    name="business_description"
                    value={form.business_description}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Tell customers what your business does, what makes it unique, and what they can expect..."
                    className={`${inputCls(!!errors.business_description)} resize-none`}
                  />
                </Field>
              </div>
            )}

            {/* ── STEP 3: Business Hours ── */}
            {step === 3 && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3.5">
                  <p className="text-[12px] text-blue-700">
                    <span className="font-semibold">💡 Tip:</span> {' '}
                    Hours are optional, but providing them helps customers plan their visits.
                  </p>
                </div>
                <HoursOfOperationForm
                  hours={form.opening_hours}
                  onHoursChange={handleHoursChange}
                />
              </div>
            )}

            {/* ── STEP 4: Location & Branding ── */}
            {step === 4 && (
              <div className="space-y-4">
                <Field label="Street Address" required error={errors.street_address}>
                  <input
                    type="text"
                    name="street_address"
                    value={form.street_address}
                    onChange={handleChange}
                    placeholder="5, Business Park, Nariman Point"
                    autoComplete="street-address"
                    className={inputCls(!!errors.street_address)}
                  />
                </Field>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="City" required error={errors.city}>
                    <input
                      type="text"
                      name="city"
                      value={form.city}
                      onChange={handleChange}
                      placeholder="Mumbai"
                      autoComplete="address-level2"
                      className={inputCls(!!errors.city)}
                    />
                  </Field>
                  <Field label="State / Province" error={errors.state_province}>
                    <input
                      type="text"
                      name="state_province"
                      value={form.state_province}
                      onChange={handleChange}
                      placeholder="Maharashtra"
                      autoComplete="address-level1"
                      className={inputCls(!!errors.state_province)}
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Postal / ZIP Code" error={errors.postal_code}>
                    <input
                      type="text"
                      name="postal_code"
                      value={form.postal_code}
                      onChange={handleChange}
                      placeholder="400021"
                      autoComplete="postal-code"
                      className={inputCls(!!errors.postal_code)}
                    />
                  </Field>
                  <Field label="Country" required error={errors.country}>
                    <input
                      type="text"
                      name="country"
                      value={form.country}
                      onChange={handleChange}
                      placeholder="India"
                      autoComplete="country-name"
                      className={inputCls(!!errors.country)}
                    />
                  </Field>
                </div>

                <div className="border-t border-slate-100 pt-4">
                  <p className="text-[12px] font-semibold text-slate-600 mb-3">Branding</p>
                  <Field
                    label="Business Logo URL"
                    required
                    error={errors.business_logo_url}
                    hint="Link to your logo image (HTTPS preferred)"
                  >
                    <input
                      type="url"
                      name="business_logo_url"
                      value={form.business_logo_url}
                      onChange={handleChange}
                      placeholder="https://tajhotels.in/logo.png"
                      className={inputCls(!!errors.business_logo_url)}
                    />
                  </Field>
                  {form.business_logo_url && (() => {
                    try { new URL(form.business_logo_url); return true; } catch { return false; }
                  })() && (
                    <div className="mt-2 flex items-center gap-3 p-2.5 bg-slate-50 border border-slate-200 rounded-lg">
                      <img
                        src={form.business_logo_url}
                        alt="Logo preview"
                        className="w-10 h-10 object-contain rounded"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                      <p className="text-[11px] text-slate-500">Logo preview</p>
                    </div>
                  )}
                </div>

                {/* Summary Card */}
                <div className="border border-indigo-200 bg-indigo-50/50 rounded-xl p-4 space-y-3">
                  <p className="text-[12px] font-bold text-indigo-700 uppercase tracking-wide">Submission Summary</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-[12px]">
                    <div>
                      <p className="text-slate-400 font-medium">Business</p>
                      <p className="text-slate-700 font-semibold truncate">{form.business_name || '—'}</p>
                    </div>
                    <div>
                      <p className="text-slate-400 font-medium">City</p>
                      <p className="text-slate-700 font-semibold truncate">{form.city || '—'}</p>
                    </div>
                    <div>
                      <p className="text-slate-400 font-medium">Account</p>
                      <p className="text-slate-700 font-semibold truncate">{form.owner_email || '—'}</p>
                    </div>
                    <div>
                      <p className="text-slate-400 font-medium">Status after submit</p>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[10px] font-bold">
                        ⏳ Pending Review
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className={`flex gap-3 mt-8 ${step > 1 ? 'justify-between' : 'justify-end'}`}>
              {step > 1 && (
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={submitting}
                  className="flex items-center gap-2 px-5 py-2.5 text-[13px] font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 hover:border-slate-300 transition-all disabled:opacity-50"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back
                </button>
              )}
              {step < TOTAL_STEPS ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="flex items-center gap-2 px-6 py-2.5 text-[13px] font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 active:scale-95 transition-all shadow-md shadow-indigo-200"
                >
                  Continue
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex items-center gap-2 px-7 py-2.5 text-[13px] font-bold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 active:scale-95 transition-all shadow-md shadow-indigo-200 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Application
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default BusinessRegistrationPage;
